import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { GoogleDriveFile, FileSearchParams, UploadResponse, SharePermission, ShareResponse } from '@/types/drive';
import useAxios from './use-axios';

/**
 * Parameters for single file upload
 */
type FileUploadData = {
  /** The file to upload */
  file: File;
  /** Optional folder name to upload to */
  folderName?: string;
  /** Optional deal ID to associate the file with */
  dealId?: string;
};

/**
 * Parameters for multiple file upload
 */
type MultipleFileUploadData = {
  /** Array of files to upload (max 10) */
  files: File[];
  /** Optional folder name to upload to */
  folderName?: string;
  /** Optional deal ID to associate the files with */
  dealId?: string;
  /** Optional callback for upload progress (0-100) */
  onProgress?: (progress: Record<string, number>) => void;
};

/**
 * Response from the list files endpoint
 */
type DriveListResponse = {
  /** Array of files from Google Drive */
  files: GoogleDriveFile[];
  /** Token for fetching the next page of results */
  nextPageToken?: string;
};

/**
 * Response from the delete file endpoint
 */
type DeleteFileResponse = {
  /** Whether the deletion was successful */
  success: boolean;
  /** Status message */
  message: string;
};

export const DRIVE_QUERY_KEYS = {
  files: {
    all: ['driveFiles'] as const,
    list: (params: FileSearchParams) => [...DRIVE_QUERY_KEYS.files.all, 'list', params] as const,
    detail: (fileId: string) => [...DRIVE_QUERY_KEYS.files.all, 'detail', fileId] as const,
  },
} as const;

/**
 * Hook to list files from Google Drive with optional filtering and pagination
 * @param params - Search parameters
 * @param params.query - Optional search query
 * @param params.pageSize - Optional number of files per page
 * @param params.pageToken - Optional token for next page
 * @param params.orderBy - Optional sort order
 * @param params.fields - Optional fields to include
 * @param params.dealId - Optional deal ID to filter files by
 */
export function useListFiles(params: FileSearchParams) {
  const axios = useAxios();

  return useInfiniteQuery<DriveListResponse>({
    queryKey: DRIVE_QUERY_KEYS.files.list(params),
    queryFn: async ({ pageParam }) => {
      const { data } = await axios.get<DriveListResponse>('/drive/list', {
        params: {
          ...params,
          pageToken: pageParam,
        },
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPageToken;
    },
    initialPageParam: undefined,
  });
}

/**
 * Hook to upload a single file to Google Drive
 * @returns Mutation for uploading a file with the following parameters:
 * @param file - The file to upload
 * @param folderName - Optional folder name to upload to
 * @param dealId - Optional deal ID to associate the file with
 */
export function useUploadFile() {
  const axios = useAxios();

  return useMutation<UploadResponse, Error, FileUploadData>({
    mutationFn: async ({ file, folderName, dealId }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (folderName)
        formData.append('folderName', folderName);
      if (dealId)
        formData.append('dealId', dealId);

      const { data } = await axios.post<UploadResponse>(
        '/drive/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return data;
    },
  });
}

/**
 * Hook to upload multiple files to Google Drive
 * @returns Mutation for uploading multiple files with the following parameters:
 * @param files - Array of files to upload (max 10)
 * @param folderName - Optional folder name to upload to
 * @param dealId - Optional deal ID to associate the files with
 * @param onProgress - Optional callback for upload progress
 */
export function useUploadMultipleFiles() {
  const axios = useAxios();

  return useMutation<UploadResponse[], Error, MultipleFileUploadData>({
    mutationFn: async ({ files, folderName, dealId, onProgress }) => {
      const formData = new FormData();
      for (const file of files) formData.append('files', file);
      if (folderName)
        formData.append('folderName', folderName);
      if (dealId)
        formData.append('dealId', dealId);

      // Initialize progress for all files
      if (onProgress) {
        const initialProgress = Object.fromEntries(
          files.map((file) => [file.name, 0]),
        );
        onProgress(initialProgress);
      }

      const { data } = await axios.post<UploadResponse[]>(
        '/drive/upload-multiple',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const overallProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);

              // Keep progress at 99% during server processing
              const progress = overallProgress >= 100 ? 99 : overallProgress;

              // Distribute progress evenly across files
              const fileProgresses = Object.fromEntries(
                files.map((file) => [
                  file.name,
                  progress,
                ]),
              );

              onProgress(fileProgresses);
            }
          },
        },
      );

      // Set final progress to 100% after server response
      if (onProgress) {
        const finalProgress = Object.fromEntries(
          files.map((file) => [file.name, 100]),
        );
        onProgress(finalProgress);
      }

      return data;
    },
  });
}

/**
 * Hook to get a file's thumbnail from Google Drive
 * @param fileId - Google Drive file ID
 * @returns Query result containing the thumbnail as a Blob
 */
export function useThumbnail(fileId: string) {
  const axios = useAxios();

  return useQuery<Blob, Error>({
    queryKey: [...DRIVE_QUERY_KEYS.files.all, 'thumbnail', fileId],
    queryFn: async () => {
      const response = await axios.get(`/drive/thumbnail/${fileId}`, {
        responseType: 'blob',
      });
      return response.data;
    },
    enabled: !!fileId,
  });
}

/**
 * Hook to download a file from Google Drive
 * @param fileId - Google Drive file ID
 * @param options - Optional react-query options
 */
export function useDownloadFile(
  fileId: string,
  options?: Omit<UseQueryOptions<Blob, Error>, 'queryKey' | 'queryFn'>,
) {
  const axios = useAxios();

  return useQuery<Blob, Error>({
    queryKey: DRIVE_QUERY_KEYS.files.detail(fileId),
    queryFn: async () => {
      const response = await axios.get(`/drive/download/${fileId}`, {
        responseType: 'blob',
      });
      return response.data;
    },
    enabled: !!fileId,
    ...options,
  });
}

/**
 * Hook to delete a file from Google Drive
 * @returns Mutation for deleting a file by its ID
 */
export function useDeleteFile() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation<DeleteFileResponse, Error, string>({
    mutationFn: async (fileId) => {
      const { data } = await axios.delete<DeleteFileResponse>(`/drive/delete/${fileId}`);
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: DRIVE_QUERY_KEYS.files.all });
    },
  });
}

/**
 * Hook to share a file with another user
 * @returns Mutation for sharing a file with the following parameters:
 * @param fileId - Google Drive file ID
 * @param permission - Share permission details (type, role, emailAddress)
 */
export function useShareFile() {
  const axios = useAxios();

  return useMutation<ShareResponse, Error, { fileId: string; permission: SharePermission }>({
    mutationFn: async ({ fileId, permission }) => {
      const { data } = await axios.post<ShareResponse>(`/drive/share/${fileId}`, permission);
      return data;
    },
  });
}
